<template>
    <div>
        <div>
            <div class="form-group">
                <label
                    for="checkout_name"
                    class="label-required"
                >
                    {{ __('Name') }}
                </label>
                <input
                    id="checkout_name"
                    v-model="data.name"
                    type="text"
                    autocomplete="name"
                    :class="{'is-invalid': errors && errors['name']}"
                    class="form-control"
                >
                <form-error
                    field="name"
                    :errors="errors"
                />
            </div>

            <div class="form-group">
                <label
                    for="checkout_email"
                    class="label-required"
                >
                    {{ __('E-mail') }}
                </label>
                <input
                    id="checkout_email"
                    v-model="data.email"
                    type="email"
                    autocomplete="email"
                    class="form-control"
                    :class="{'is-invalid': errors && errors['email']}"
                >
                <form-error
                    field="email"
                    :errors="errors"
                />
            </div>

            <div class="form-group">
                <label
                    for="checkout_phone_number"
                    class="label-required"
                >
                    {{ __('Phone') }}
                </label>
                <phone-number-field
                    :fix-country-code="checkoutData.phoneNumberFixCountryCode"
                    :country-code="data.phone_number['country_code']"
                    :prefix="data.phone_number.prefix"
                    :line-number="data.phone_number['line_number']"
                    :errors="errors"
                    @input="setPhoneNumber($event)"
                />
            </div>

            <h2 class="cart-title">
                {{ __('Billing data') }}
            </h2>

            <div class="form-group">
                <label
                    for="checkout_billing_name"
                    class="label-required"
                >
                    {{ __('Billing name') }}
                </label>
                <input
                    id="checkout_billing_name"
                    v-model="data.billing_name"
                    type="text"
                    autocomplete="billing_name"
                    class="form-control"
                    :class="{'is-invalid': errors && errors['billing_name']}"
                >
                <form-error
                    field="billing_name"
                    :errors="errors"
                />
            </div>

            <div class="form-group">
                <label
                    for="checkout_billing_zip"
                    class="label-required"
                >
                    {{ __('Billing zip') }}
                </label>
                <input
                    id="checkout_billing_zip"
                    v-model="data.billing_zip"
                    type="text"
                    autocomplete="billing_zip"
                    class="form-control"
                    :class="{'is-invalid': errors && errors['billing_zip']}"
                >
                <form-error
                    field="billing_zip"
                    :errors="errors"
                />
            </div>

            <div class="form-group">
                <label
                    for="checkout_billing_city"
                    class="label-required"
                >
                    {{ __('Billing city') }}
                </label>
                <input
                    id="checkout_billing_city"
                    v-model="data.billing_city"
                    type="text"
                    autocomplete="billing_city"
                    class="form-control"
                    :class="{'is-invalid': errors && errors['billing_city']}"
                >
                <form-error
                    field="billing_city"
                    :errors="errors"
                />
            </div>

            <div class="form-row">
                <div class="form-group col-md">
                    <label
                        for="checkout_billing_address"
                        class="label-required"
                    >
                        {{ __('Billing address') }}
                    </label>
                    <input
                        id="checkout_billing_address"
                        v-model="data.billing_address"
                        type="text"
                        autocomplete="billing_address"
                        :class="{'is-invalid': errors && errors['billing_address']}"
                        class="form-control"
                    >
                    <form-error
                        field="billing_address"
                        :errors="errors"
                    />
                </div>
                <div class="form-group col-md">
                    <label
                        for="checkout_billing_house_number"
                        class="label-required"
                    >{{ __('Billing house number') }}
                    </label>
                    <input
                        id="checkout_billing_house_number"
                        v-model="data.billing_house_number"
                        type="text"
                        autocomplete="billing_house_number"
                        :class="{'is-invalid': errors && errors['billing_house_number']}"
                        class="form-control"
                    >
                    <form-error
                        field="billing_house_number"
                        :errors="errors"
                    />
                </div>
            </div>
        </div>

        <div class="form-group">
            <label for="checkout_vat_number">{{ __('Vat number') }}</label>
            <input
                id="checkout_vat_number"
                v-model="data.vat_number"
                type="text"
                autocomplete="vat_number"
                class="form-control"
                :class="{'is-invalid': errors && errors['vat_number']}"
            >
            <form-error
                field="vat_number"
                :errors="errors"
            />
        </div>

        <div
            v-if="shippable && !selectedShippingModeHasPickupPoints"
            class="shipping-data"
        >
            <h2>
                {{ __('Shipping data') }}
            </h2>

            <div class="form-group">
                <div class="form-check">
                    <input
                        id="checkout_same_as_billing"
                        v-model="data.same_as_billing"
                        type="checkbox"
                        name="same_as_billing"
                        value="1"
                        class="form-check-input"
                    >
                    <label
                        class="form-check-label"
                        for="checkout_same_as_billing"
                    >
                        {{ __('Same as billing address') }}
                    </label>
                </div>
            </div>

            <div v-show="data.same_as_billing === false">
                <div class="form-group">
                    <label
                        for="checkout_shipping_name"
                        class="label-required"
                    >
                        {{ __('Shipping name') }}
                    </label>
                    <input
                        id="checkout_shipping_name"
                        v-model="data.shipping_name"
                        type="text"
                        autocomplete="shipping_name"
                        class="form-control"
                        :class="{'is-invalid': errors && errors['shipping_name']}"
                    >
                    <form-error
                        field="shipping_name"
                        :errors="errors"
                    />
                </div>

                <div class="form-group">
                    <label
                        for="checkout_shipping_zip"
                        class="label-required"
                    >
                        {{ __('Shipping zip') }}
                    </label>
                    <input
                        id="checkout_shipping_zip"
                        v-model="data.shipping_zip"
                        type="text"
                        autocomplete="shipping_zip"
                        class="form-control"
                        :class="{'is-invalid': errors && errors['shipping_zip']}"
                    >
                    <form-error
                        field="shipping_zip"
                        :errors="errors"
                    />
                </div>

                <div class="form-group">
                    <label
                        for="checkout_shipping_city"
                        class="label-required"
                    >
                        {{ __('Shipping city') }}
                    </label>
                    <input
                        id="checkout_shipping_city"
                        v-model="data.shipping_city"
                        type="text"
                        autocomplete="shipping_city"
                        class="form-control"
                        :class="{'is-invalid': errors && errors['shipping_city']}"
                    >
                    <form-error
                        field="shipping_city"
                        :errors="errors"
                    />
                </div>

                <div class="form-row">
                    <div class="form-group col-md">
                        <label
                            for="checkout_shipping_address"
                            class="label-required"
                        >
                            {{ __('Shipping address') }}
                        </label>
                        <input
                            id="checkout_shipping_address"
                            v-model="data.shipping_address"
                            type="text"
                            autocomplete="shipping_address"
                            :class="{'is-invalid': errors && errors['shipping_address']}"
                            class="form-control"
                        >
                        <form-error
                            field="shipping_address"
                            :errors="errors"
                        />
                    </div>
                    <div class="form-group col-md">
                        <label
                            for="checkout_shipping_house_number"
                            class="label-required"
                        >{{ __('Shipping house number') }}
                        </label>
                        <input
                            id="checkout_shipping_house_number"
                            v-model="data.shipping_house_number"
                            type="text"
                            autocomplete="shipping_house_number"
                            :class="{'is-invalid': errors && errors['shipping_house_number']}"
                            class="form-control"
                        >
                        <form-error
                            field="shipping_house_number"
                            :errors="errors"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import guestCustomerDataBase from '../../../../mixins/checkout/guestCustomerDataBase'

export default {
    name: 'GuestCustomerDataForm',
    mixins: [guestCustomerDataBase]
}
</script>
